import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import {
  MessageSquarePlus,
  Pencil,
  Search,
  UserPlus,
  Users,
  WifiOff,
} from "lucide-react";
import { useCurrentUser } from "../../backend/queries/auth";
import { normalizeRole } from "../../components/shell/navigation";
import { useChatSocket } from "../../backend/chat/ChatSocketProvider";
import { useConversations } from "../../backend/queries/chat";
import { participantName } from "../../backend/api/chat";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import type { ChatConversation } from "../../backend/api/chat";
import { ConversationList } from "./ConversationList";
import {
  MessageSearchFilters,
  MessageSearchResults,
  emptySearch,
  isSearching,
} from "./MessageSearch";
import { ConversationPane, titleForConversation } from "./ConversationPane";
import { GroupMembersDialog } from "./GroupMembersDialog";
import { IncomingMessageAnnouncer } from "./IncomingMessageAnnouncer";
import { RenameGroupDialog } from "./RenameGroupDialog";
import { StartConversationDialog } from "./StartConversationDialog";

type InboxFilter = "all" | "unread" | "dm" | "group" | "project";

const INBOX_FILTERS: { value: InboxFilter; label: string }[] = [
  { value: "all", label: "All conversations" },
  { value: "unread", label: "Unread" },
  { value: "dm", label: "Direct messages" },
  { value: "group", label: "Group chats" },
  { value: "project", label: "Project chats" },
];

const matchesInboxFilter = (
  conversation: ChatConversation,
  filter: InboxFilter,
) => {
  switch (filter) {
    case "unread":
      return conversation.unread_count > 0;
    case "dm":
      return conversation.type === "dm";
    case "group":
      return conversation.type === "group" && conversation.project_id === null;
    case "project":
      return conversation.project_id !== null;
    default:
      return true;
  }
};

/**
 * Full-page inbox for Admin and Agent.
 *
 * Staff work across many conversations at once, so they get a list beside a
 * thread rather than the floating dock clients use.
 */
const Messages = () => {
  const { data: user } = useCurrentUser();
  const conversations = useConversations();
  const { status, reconnect } = useChatSocket();
  const [searchParams, setSearchParams] = useSearchParams();
  const [startOpen, setStartOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [search, setSearch] = useState(emptySearch);
  const [inboxFilter, setInboxFilter] = useState<InboxFilter>("all");
  const [focusMessageId, setFocusMessageId] = useState<string | null>(null);
  const clearFocus = useCallback(() => setFocusMessageId(null), []);
  const paneRef = useRef<HTMLDivElement>(null);

  const currentUserId = user?.id ?? null;
  // The route already keeps clients out. Checking the role here as well keeps
  // the control honest if this page is ever reached another way.
  const role = normalizeRole(user?.role);
  const isStaff = role !== null && role !== "Client";
  // The selected thread lives in the URL so a "Message client" link from a
  // project can deep-link straight into the right conversation.
  const selectedId = searchParams.get("conversation");

  const select = useCallback(
    (id: string | null) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (id) {
            next.set("conversation", id);
          } else {
            next.delete("conversation");
          }
          return next;
        },
        { replace: true },
      );
      // Below `md` the list and the thread stack, so the thread opens below
      // the fold. Bring it into view or a tap looks like it did nothing.
      const stacked =
        window.matchMedia?.("(max-width: 767px)").matches ||
        Boolean(paneRef.current?.offsetParent);
      if (id && stacked) {
        requestAnimationFrame(() =>
          paneRef.current?.scrollIntoView?.({ block: "start" }),
        );
      }
    },
    [setSearchParams],
  );

  const selected = useMemo(
    () =>
      conversations.data?.find(
        (conversation) => conversation.id === selectedId,
      ) ?? undefined,
    [conversations.data, selectedId],
  );

  // A conversation id that is not in the list is stale, usually because the
  // caller was removed from it. Clearing it avoids a permanently empty pane.
  useEffect(() => {
    if (!selectedId || conversations.isPending || conversations.error) return;
    if (!selected) select(null);
  }, [
    conversations.error,
    conversations.isPending,
    select,
    selected,
    selectedId,
  ]);

  const participants = selected?.participants ?? [];
  const searching = isSearching(search);

  const filteredConversations = useMemo(
    () =>
      (conversations.data ?? []).filter((conversation) =>
        matchesInboxFilter(conversation, inboxFilter),
      ),
    [conversations.data, inboxFilter],
  );

  const conversationTitleById = useCallback(
    (id: string) => {
      const conversation = conversations.data?.find((item) => item.id === id);
      return conversation
        ? titleForConversation(conversation, currentUserId)
        : "Conversation";
    },
    [conversations.data, currentUserId],
  );

  const openResult = useCallback(
    (conversationId: string, messageId: string) => {
      setFocusMessageId(messageId);
      select(conversationId);
    },
    [select],
  );

  return (
    <div className="min-h-screen bg-canvas p-4 sm:p-6">
      {/* The two columns depend on how much room the page has, in rems, so they
          stack when text is enlarged, not only when the window is narrow. A
          breakpoint in pixels cannot tell the difference. */}
      <div className="mx-auto flex max-w-[1600px] flex-col gap-4 [container-type:inline-size]">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">Messages</h1>
          <Button
            className="min-h-touch md:min-h-0"
            onClick={() => setStartOpen(true)}
          >
            <MessageSquarePlus className="mr-2 h-4 w-4" aria-hidden="true" />
            New conversation
          </Button>
        </div>

        <IncomingMessageAnnouncer
          conversations={conversations.data}
          currentUserId={currentUserId}
          titleFor={(conversation) =>
            titleForConversation(conversation, currentUserId)
          }
        />

        {status !== "open" ? (
          <div
            role="status"
            className="flex items-center gap-2 rounded-md border bg-muted px-3 py-2 text-sm text-muted-foreground"
          >
            <WifiOff className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="flex-1">
              {status === "connecting"
                ? "Connecting to live messages"
                : "You are offline. New messages may be delayed."}
            </span>
            {status === "offline" ? (
              <Button variant="outline" size="sm" onClick={reconnect}>
                Retry
              </Button>
            ) : null}
          </div>
        ) : null}

        <Card className="grid min-h-[32rem] grid-cols-1 overflow-hidden [@container(min-width:40rem)]:h-[calc(100vh-12rem)] [@container(min-width:40rem)]:grid-cols-[20rem_minmax(0,1fr)]">
          <div className="flex min-h-0 flex-col border-b [@container(min-width:40rem)]:border-b-0 [@container(min-width:40rem)]:border-r">
            <div
              role="search"
              aria-label="Search messages"
              className="space-y-2 border-b p-2"
            >
              <div className="relative">
                <Label htmlFor="inbox-search" className="sr-only">
                  Search messages
                </Label>
                <Search
                  aria-hidden="true"
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  id="inbox-search"
                  type="search"
                  value={search.text}
                  placeholder="Search messages"
                  className="pl-9"
                  onChange={(event) =>
                    setSearch({ ...search, text: event.target.value })
                  }
                />
              </div>
              {search.text || search.kind !== "any" ? (
                <MessageSearchFilters search={search} onChange={setSearch} />
              ) : (
                <Select
                  value={inboxFilter}
                  onValueChange={(value) =>
                    setInboxFilter(value as InboxFilter)
                  }
                >
                  <SelectTrigger
                    aria-label="Show"
                    className="h-auto min-h-touch text-xs md:min-h-9"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INBOX_FILTERS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {searching ? (
                <MessageSearchResults
                  search={search}
                  conversationTitle={conversationTitleById}
                  currentUserId={currentUserId}
                  onOpen={openResult}
                />
              ) : (
                <ConversationList
                  conversations={filteredConversations}
                  titleFor={(conversation) =>
                    titleForConversation(conversation, currentUserId)
                  }
                  selectedId={selectedId}
                  onSelect={select}
                  currentUserId={currentUserId}
                  isLoading={conversations.isPending}
                  error={conversations.error as Error | null}
                  onRetry={() => void conversations.refetch()}
                  onStartConversation={
                    inboxFilter === "all" ? () => setStartOpen(true) : undefined
                  }
                  emptyMessage={
                    inboxFilter === "all"
                      ? "No conversations yet. Start one with a client or a colleague."
                      : "No conversations match this filter."
                  }
                />
              )}
            </div>
          </div>

          {/* Scroll target for phones, where the thread stacks below the list. */}
          <div
            ref={paneRef}
            aria-hidden="true"
            className="scroll-mt-header [@container(min-width:40rem)]:hidden"
          />
          <ConversationPane
            conversationId={selected?.id ?? null}
            conversation={selected}
            currentUserId={currentUserId}
            focusMessageId={focusMessageId}
            onFocused={clearFocus}
            className="h-[calc(100dvh-7rem)] min-h-0 min-w-0 [@container(min-width:40rem)]:h-auto"
            header={(mediaControl) =>
              selected ? (
                <header className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-4 py-3">
                  <div className="min-w-[min(10rem,100%)] flex-1">
                    <h2 className="truncate text-sm font-semibold">
                      {titleForConversation(selected, currentUserId)}
                    </h2>
                    {selected.type === "group" ? (
                      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Users
                          className="h-3 w-3 shrink-0"
                          aria-hidden="true"
                        />
                        <span className="truncate">
                          {participants.length} participant
                          {participants.length === 1 ? "" : "s"}
                          {participants.length
                            ? ` · ${participants.map(participantName).join(", ")}`
                            : ""}
                        </span>
                      </p>
                    ) : null}
                  </div>
                  {selected.type === "group" ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setRenameOpen(true)}
                      className="h-auto min-h-touch shrink-0 gap-1.5 text-xs md:h-7 md:min-h-0"
                    >
                      <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                      Rename
                    </Button>
                  ) : null}
                  {selected.type === "group" && isStaff ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setMembersOpen(true)}
                      className="h-auto min-h-touch shrink-0 gap-1.5 text-xs md:h-7 md:min-h-0"
                    >
                      <UserPlus className="h-3.5 w-3.5" aria-hidden="true" />
                      Members
                    </Button>
                  ) : null}
                  {mediaControl}
                </header>
              ) : null
            }
          />
        </Card>
      </div>

      <StartConversationDialog
        open={startOpen}
        onOpenChange={setStartOpen}
        onCreated={select}
      />
      {selected?.type === "group" ? (
        <RenameGroupDialog
          open={renameOpen}
          onOpenChange={setRenameOpen}
          conversationId={selected.id}
          currentTitle={titleForConversation(selected, currentUserId)}
        />
      ) : null}
      {selected?.type === "group" && isStaff ? (
        <GroupMembersDialog
          open={membersOpen}
          onOpenChange={setMembersOpen}
          conversationId={selected.id}
          participants={selected.participants}
          currentUserId={currentUserId}
          projectLinked={selected.project_id !== null}
        />
      ) : null}
    </div>
  );
};

export default Messages;
